import { Card, Dropdown, Badge, ButtonGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Button from 'components/base/Button';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import {
  fetchNotifications,
  markAllAsRead,
  markAsRead,
  selectNotifications,
  selectUnreadCount,
  selectNotificationsLoading
} from 'store/notificationsSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faUser,
  faClock,
  faTag,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import FeatherIcon from 'feather-icons-react';

const NotificationDropdownMenu = ({ className }: { className?: string }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const loading = useSelector(selectNotificationsLoading);

  // Filter state
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  useEffect(() => {
    dispatch(fetchNotifications(false)); // Fetch all notifications
  }, [dispatch]);

  // Filter notifications based on selected tab
  const filteredNotifications =
    filter === 'unread' ? notifications.filter(n => !n.is_read) : notifications;

  const handleMarkAllAsRead = async () => {
    try {
      await dispatch(markAllAsRead()).unwrap();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Handle marking a single notification as read
  const handleMarkSingleAsRead = async (notificationId: number) => {
    try {
      await dispatch(markAsRead(notificationId)).unwrap();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Handle notification click - mark as read and navigate
  const handleNotificationClick = async (
    notification: (typeof notifications)[0]
  ) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await handleMarkSingleAsRead(notification.id);
    }

    // Navigate based on entity type
    if (notification.related_entity_type === 'asset_request') {
      navigate('/my-requests');
    } else if (notification.related_entity_type === 'asset') {
      navigate('/assets');
    }
  };

  // Define type for type configuration
  interface TypeConfig {
    icon: IconProp;
    color: string;
    bg: string;
    badgeBg: string; // Added for original dark colors
  }

  // Get type icon and color - Updated with original dark colors
  const getTypeConfig = (type: string): TypeConfig => {
    const configs: Record<string, TypeConfig> = {
      info: {
        icon: faCircleInfo,
        color: '#0dcaf0',
        bg: 'rgba(13, 202, 240, 0.1)',
        badgeBg: 'info' // Original Bootstrap info color
      },
      success: {
        icon: faCircleCheck,
        color: '#198754',
        bg: 'rgba(25, 135, 84, 0.1)',
        badgeBg: 'success' // Original Bootstrap success color
      },
      warning: {
        icon: faCircleExclamation,
        color: '#ffc107',
        bg: 'rgba(255, 193, 7, 0.1)',
        badgeBg: 'warning' // Original Bootstrap warning color
      },
      error: {
        icon: faCircleXmark,
        color: '#dc3545',
        bg: 'rgba(220, 53, 69, 0.1)',
        badgeBg: 'danger' // Original Bootstrap danger color
      }
    };
    return configs[type] || configs.info;
  };

  // Format relative time
  const formatTime = (dateString: string): string => {
    // Ensure proper UTC parsing - add 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const date = new Date(utcString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Extract user name from message
  const extractUserName = (message: string): string => {
    const nameMatch = message.match(/(\w+)\s+(?:requested|approved|rejected)/i);
    return nameMatch ? nameMatch[1] : 'User';
  };

  return (
    <Dropdown.Menu
      align="end"
      className={classNames(
        className,
        'navbar-dropdown-caret py-0 notification-dropdown-menu shadow border'
      )}
    >
      <Card className="position-relative border-0">
        <Card.Header className="p-3 border-bottom">
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <h5 className="text-body-emphasis mb-0">
              Notifications
              {unreadCount > 0 && (
                <Badge bg="primary" pill className="ms-2">
                  {unreadCount} new
                </Badge>
              )}
            </h5>
            <Button
              variant="link"
              className="p-0 fs-9 fw-semi-bold text-primary text-nowrap"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0 || loading}
            >
              Mark all as read
            </Button>
          </div>

          {/* Filter Tabs */}
          <ButtonGroup size="sm" className="w-100">
            <Button
              variant={filter === 'all' ? 'primary' : 'outline-primary'}
              onClick={() => setFilter('all')}
              className="fs-10 py-1"
            >
              All ({notifications.length})
            </Button>
            <Button
              variant={filter === 'unread' ? 'primary' : 'outline-primary'}
              onClick={() => setFilter('unread')}
              className="fs-10 py-1"
            >
              Unread ({unreadCount})
            </Button>
          </ButtonGroup>
        </Card.Header>

        <Card.Body className="p-0 notification-card-body">
          {loading ? (
            <div className="text-center p-5">
              <div
                className="spinner-border spinner-border-sm text-primary"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-5 px-4">
              {filter === 'unread' ? (
                <>
                  <div
                    className="mb-3 d-flex justify-content-center"
                    style={{ fontSize: '48px' }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="text-success"
                    />
                  </div>
                  <h6 className="text-body-emphasis mb-0">All Caught Up!</h6>
                </>
              ) : (
                <>
                  <div
                    className="mb-3 d-flex justify-content-center"
                    style={{ fontSize: '48px' }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="text-body-tertiary"
                    />
                  </div>
                  <h6 className="text-body-emphasis mb-0">No Notifications</h6>
                </>
              )}
            </div>
          ) : (
            filteredNotifications.slice(0, 8).map(notification => {
              const typeConfig = getTypeConfig(notification.type);
              const userName = extractUserName(notification.message);

              return (
                <div
                  key={notification.id}
                  // onClick={() => handleNotificationClick(notification)}
                  className={classNames(
                    'px-2 px-sm-3 py-3 notification-card position-relative border-bottom',
                    !notification.is_read ? 'unread' : 'read'
                  )}
                  style={{
                    cursor: 'pointer'
                  }}
                >
                  <div className="d-flex align-items-start gap-2">
                    {/* User Icon - Person without face */}
                    <div
                      className="flex-shrink-0 d-flex align-items-center justify-content-center"
                      style={{
                        width: '36px',
                        height: '36px',
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #e9ecef',
                        borderRadius: '50%'
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faUserCircle}
                        className="text-body-tertiary"
                        style={{ fontSize: '20px' }}
                      />
                    </div>

                    {/* Notification Content */}
                    <div className="flex-grow-1 position-relative">
                      {/* Header with title and type */}
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <h6
                          className={classNames(
                            'mb-0 fs-9 fw-semi-bold',
                            notification.is_read
                              ? 'text-body-secondary'
                              : 'text-body-emphasis'
                          )}
                        >
                          {notification.title}
                        </h6>

                        {/* Original dark colored badge */}
                        <Badge
                          bg={typeConfig.badgeBg}
                          pill
                          className="fs-10 me-2"
                          style={{
                            fontWeight: '600',
                            padding: '2px 8px'
                          }}
                        >
                          {notification.type.charAt(0).toUpperCase() +
                            notification.type.slice(1)}
                        </Badge>

                        {/* Unread Indicator Dot - Right Side */}
                        {!notification.is_read && (
                          <div
                            className="unread-indicator"
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#0d6efd',
                              marginLeft: '4px'
                            }}
                            title="Unread"
                          />
                        )}

                        {/* Reveal Dropdown for "Mark as read" */}
                        {!notification.is_read && (
                          <RevealDropdownTrigger>
                            <RevealDropdown>
                              <div className="py-2">
                                <button
                                  className="dropdown-item text-primary"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleMarkSingleAsRead(notification.id);
                                  }}
                                >
                                  <FeatherIcon
                                    icon="check"
                                    size={14}
                                    className="me-2"
                                  />
                                  Mark as read
                                </button>
                              </div>
                            </RevealDropdown>
                          </RevealDropdownTrigger>
                        )}
                      </div>

                      {/* Message */}
                      <p
                        className={classNames(
                          'mb-2 fs-9',
                          notification.is_read
                            ? 'text-body-secondary'
                            : 'text-body'
                        )}
                        style={{
                          lineHeight: '1.4'
                        }}
                      >
                        {notification.message}
                      </p>

                      {/* Metadata Row */}
                      <div className="d-flex align-items-center gap-3">
                        {/* User */}
                        <div className="d-flex align-items-center gap-1">
                          <FontAwesomeIcon
                            icon={faUser}
                            className="text-body-tertiary"
                            style={{ fontSize: '10px' }}
                          />
                          <small className="text-body-tertiary fs-10">
                            {userName}
                          </small>
                        </div>

                        {/* Entity Type */}
                        {notification.related_entity_type && (
                          <div className="d-flex align-items-center gap-1">
                            <FontAwesomeIcon
                              icon={faTag}
                              className="text-body-tertiary"
                              style={{ fontSize: '10px' }}
                            />
                            <small className="text-body-tertiary fs-10 text-capitalize">
                              {notification.related_entity_type.replace(
                                '_',
                                ' '
                              )}
                            </small>
                          </div>
                        )}

                        {/* Time */}
                        <div className="d-flex align-items-center gap-1">
                          <FontAwesomeIcon
                            icon={faClock}
                            className="text-body-tertiary"
                            style={{ fontSize: '10px' }}
                          />
                          <small className="text-body-tertiary fs-10">
                            {formatTime(notification.created_at)}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </Card.Body>

        <Card.Footer className="p-2 border-top text-center">
          {notifications.length > 0 && (
            <Dropdown.Item
              as="button"
              onClick={() => navigate('/notifications')}
              className="btn btn-sm btn-outline-primary w-100 py-2 fs-9 fw-semi-bold text-center"
              style={{ border: '1px solid #0d6efd' }}
            >
              View All Notifications
              <FontAwesomeIcon icon={faCircleInfo} className="ms-2" />
            </Dropdown.Item>
          )}
        </Card.Footer>
      </Card>
    </Dropdown.Menu>
  );
};

export default NotificationDropdownMenu;
